import { PlusOutlined, SortAscendingOutlined } from '@ant-design/icons';
import { Button, Drawer, message, Collapse, Space, Popconfirm, Input, Tag } from 'antd';
import React, { useState, useEffect, useMemo } from 'react';
import { $t } from '~/i18n.js';
import { api } from '~/lib/api.js';
import { PriveKistForm } from './PriveKistForm.jsx';
import { HelpLine } from '~/components/help/helpLine.jsx';
import { DateFormat } from '~/components/date/DateFormat.jsx';

const { Panel } = Collapse;

export const ViewUnlistedMaterialDrawer = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kisten, setKisten] = useState([]);
  const [activeKey, setActiveKey] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortType, setSortType] = useState('flight'); // 'flight' or 'name'

  // Filter and sort kisten based on search term and sortType
  const filteredKisten = useMemo(() => {
    let filtered = kisten;
    
    // Filter based on search term
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = kisten.filter(kist => {
        const registratie = (kist.registratie || '').toLowerCase();
        const callsign = (kist.callsign || '').toLowerCase();
        const type = (kist.type || '').toLowerCase();
        const flarm = (kist.flarm || '').toLowerCase();
        
        return registratie.includes(lowerSearchTerm) ||
               callsign.includes(lowerSearchTerm) ||
               type.includes(lowerSearchTerm) ||
               flarm.includes(lowerSearchTerm);
      });
    }
    
    // Sort based on sortType
    return filtered.sort((a, b) => {
      // Handle new kists (always put them first)
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      if (a.isNew && b.isNew) return 0;
      
      if (sortType === 'name') {
        // Sort by name (callsign and registratie)
        const nameA = `${a.callsign || ''} ${a.registratie || ''}`.toLowerCase();
        const nameB = `${b.callsign || ''} ${b.registratie || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      } else {
        // Sort by date_last_flight (most recent first, null values last)
        
        // Handle null values (put them at the end)
        if (a.date_last_flight === null && b.date_last_flight === null) return 0;
        if (a.date_last_flight === null) return 1;
        if (b.date_last_flight === null) return -1;
        
        // Sort by date (most recent first)
        return new Date(b.date_last_flight).getTime() - new Date(a.date_last_flight).getTime();
      }
    });
  }, [kisten, searchTerm, sortType]);

  const fetchKisten = async () => {
    try {
      setLoading(true);
      const data = await api.get('materiaal/unlisted_kisten.json');
      if (data) {
        setKisten(data);
      }
    } catch (error) {
      message.error($t('Fout bij het ophalen van kisten'));
      console.error('Error fetching kisten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    const newKist = {
      id: 'new',
      registratie: '',
      callsign: '',
      type: '',
      category: 'prive',
      isNew: true,
      date_last_flight: -1 // Use -1 to indicate a new kist without a last flight date
    };
    setKisten([newKist, ...kisten]);
    setActiveKey(['new']);
  };

  const handleSave = async (kistData) => {
    try {
      setLoading(true);
      let response
      if (kistData.isNew) {
        // Create new kist
        const { isNew, ...dataToSend } = kistData;
        response = await api.post2('materiaal/unlisted_kisten.json', dataToSend);
      } else {
        // Update existing kist
        response = await api.put('materiaal/unlisted_kisten.json', kistData);
      }

      if (response.data) {
        message.success($t('Kist succesvol opgeslagen'));
        await fetchKisten();
        // Close the panel for this kist
        setActiveKey(prev => prev.filter(key => key !== kistData.id));
      }
    } catch (error) {
      message.error($t('Fout bij het opslaan van kist'));
      console.error('Error saving kist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (kistId) => {
    try {
      setLoading(true);
      const response = await api.delete('materiaal/unlisted_kisten.json', { id: kistId });
      
      if (response?.data?.deleted) {
        message.success($t('Kist succesvol verwijderd'));
        await fetchKisten();
      }
    } catch (error) {
      message.error($t('Fout bij het verwijderen van kist'));
      console.error('Error deleting kist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (kistId) => {
    if (kistId === 'new') {
      // Remove the new kist from the list
      setKisten(prev => prev.filter(k => k.id !== 'new'));
    }
    // Close the panel
    setActiveKey(prev => prev.filter(key => key !== kistId));
  };

  useEffect(() => {
    if (showDrawer) {
      fetchKisten();
    }
  }, [showDrawer]);

  const openDrawer = () => {
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setActiveKey([]);
  };

  return (
    <>
      <Button
        onClick={openDrawer}
        size="small"
        type="default"
      >
        {$t("Prive kisten")}
      </Button>

      <Drawer
        title={$t("Prive kisten beheren")}
        placement="right"
        width={600}
        open={showDrawer}
        onClose={closeDrawer}
        loading={loading}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            size="small"
          >
            {$t("Toevoegen")}
          </Button>
        }
      >
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input.Search
            placeholder={$t("Zoek op registratie, callsign, type of FlarmID...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            size="small"
            style={{ flex: 1 }}
          />
          <Tag 
            icon={<SortAscendingOutlined />}
            color={sortType === 'flight' ? 'blue' : 'default'}
            className='clickable'
            onClick={() => setSortType('flight')}
          >
            {$t("Vluchten")}
          </Tag>
          <Tag 
            icon={<SortAscendingOutlined />}
            color={sortType === 'name' ? 'blue' : 'default'}
            className='clickable'
            onClick={() => setSortType('name')}
          >
            {$t("Naam")}
          </Tag>
          
        </div>

        <Collapse 
          activeKey={activeKey}
          onChange={setActiveKey}
          accordion={true}
        >
          {filteredKisten.map((kist) => (
            <Panel 
              header={
                kist.isNew ? (
                  <p>{$t("Nieuwe kist")}</p>
                ) : (
                  <span>{kist.callsign}: {kist.registratie} <span className='gray'>{kist.type}</span></span>
                )
              }
              extra={
                !kist.isNew && (
                  <Tag>
                    {kist.date_last_flight ? (
                      <DateFormat date={kist.date_last_flight} />
                    ) : (
                      $t("geen vluchten")
                    )}
                  </Tag>
                )
              }
              key={kist.id}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <PriveKistForm 
                  kist={kist}
                  onSave={handleSave}
                  onCancel={() => handleCancel(kist.id)}
                  loading={loading}
                />
                
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button 
                    onClick={() => handleCancel(kist.id)}
                    disabled={loading}
                  >
                    {$t("Annuleren")}
                  </Button>
                  
                  <Button
                    type="primary"
                    onClick={() => {
                      const formElement = document.querySelector(`[data-kist-id="${kist.id}"] form`);
                      if (formElement) {
                        formElement.dispatchEvent(new Event('submit', { bubbles: true }));
                      }
                    }}
                    loading={loading}
                  >
                    {$t("Opslaan")}
                  </Button>
                  
                  {!kist.isNew && (
                    <Popconfirm
                      title={$t("Weet je zeker dat je deze kist wilt verwijderen?")}
                      onConfirm={() => handleDelete(kist.id)}
                      okText={$t("Ja")}
                      cancelText={$t("Nee")}
                    >
                      <Button 
                        danger
                        disabled={loading}
                      >
                        {$t("Verwijder")}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </Space>
            </Panel>
          ))}
        </Collapse>

        {filteredKisten.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            {searchTerm ? 
              $t("Geen kisten gevonden die overeenkomen met '${search}'", { search: searchTerm }) :
              $t("Geen prive kisten gevonden")
            }
          </div>
        )}

        <HelpLine style={{ marginTop: 16 }}>
          {$t('Dit overzicht bevat (prive) kisten die geen onderhoudsmodule geactiveerd hebben, wil je die activeren voeg de registratie toe bij het materiaal overzicht.')}
        </HelpLine>
      </Drawer>
    </>
  );
};
